import { useMemo, useState } from "react";
import { useRouter } from "next/router";
import { Anchor } from "../../components/anchor";
import { IconLocation } from "../../components/icons/icon-location";
import { IconWhatsapp } from "../../components/icons/icon-whatsapp";
import { SellerProfile } from "../../stores/seller-store/seller-store.model";
import { useCopyToClipboard } from "../../utils/hooks/useCopyToClipboard";
import { normalizePhoneNumber } from "../../utils/number";
import { SellerPageRouter } from "../seller/page-router.props";
import { ImageViewer } from "../product-detail/components/image-carousel/image-viewer";

interface Props {
  sellerData: SellerProfile;
}

export const CredistoreInfoBody = ({ sellerData }: Props) => {
  const router: SellerPageRouter = useRouter();
  const storeName = useMemo(() => router.query.seller, [router.query.seller]);
  const { copyToClipboard } = useCopyToClipboard();
  const [showImageViewer, setShowImageViewer] = useState<boolean>(false);

  const renderAvatar = () => (
    <>
      {sellerData?.profilePicture ? (
        <img
          src={sellerData?.profilePicture}
          alt={sellerData?.User.name}
          width={128}
          height={128}
          className="rounded-full w-28 h-28 mx-auto drop-shadow-sm cursor-pointer"
          onClick={() => setShowImageViewer(true)}
          id="seller-info-img-profile"
          data-testid="seller-info-img-profile"
        />
      ) : (
        <div className="w-28 h-28 m-auto rounded-full drop-shadow-sm overflow-hidden">
          <img
            src="/img/common/default-merchant.svg"
            alt={sellerData?.User.name}
            width={128}
            height={128}
            id="seller-info-img-profile"
            data-testid="seller-info-img-profile"
            onClick={() => setShowImageViewer(true)}
            className="cursor-pointer"
          />
        </div>
      )}
      <h1 className="mt-2 px-2 text-title-md text-gray font-medium">
        {sellerData?.User.name}
      </h1>
      {sellerData?.latitude && sellerData?.longitude && (
        <Anchor
          className="flex items-center text-label-md mt-2"
          href={`https://www.google.com/maps/search/?api=1&query=${sellerData?.latitude},${sellerData?.longitude}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          <IconLocation
            className="text-primary mr-1.5"
            width={16}
            height={16}
          />
          {!!sellerData?.City?.name && (
            <span className="text-label-sm">{sellerData?.City.name}</span>
          )}
        </Anchor>
      )}
    </>
  );

  const renderStoreProductOrderCount = () => (
    <div className="flex mt-2">
      <span className="p-2 text-label-sm bg-white rounded shadow-light">
        {sellerData?.countProducts || 0} Produk
      </span>
      <span className="ml-4 p-2 text-label-sm bg-white rounded shadow-light">
        {sellerData?.countOrders || 0} Terjual
      </span>
    </div>
  );

  const renderContactInfo = () => (
    <div className="mt-4 flex items-end w-full border-b border-b-line-gray pb-3">
      <div className="text-left w-full">
        <div className="text-label-sm font-normal text-black text-opacity-60">
          Nomor Telepon
        </div>
        <div className="mt-1.5 text-label-lg">
          {normalizePhoneNumber(sellerData?.User.phone)}
        </div>
      </div>
      <Anchor
        href={`https://wa.me/${normalizePhoneNumber(
          sellerData?.User.phone
        )}?text=Hai%20${storeName}!`}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center mr-2 px-2 py-3 h-6 bg-white border rounded text-primary border-primary"
        id="seller-info-wa-btn"
      >
        <IconWhatsapp width={16} height={16} className="text-green" />
        <span className="pl-1 text-label-sm tracking-wide">Hubungi</span>
      </Anchor>
    </div>
  );

  const renderAddressInfo = () => (
    <>
      <div className="mt-4 flex items-end w-full border-b border-b-line-gray pb-3">
        <div className="text-left w-full">
          <div className="text-label-sm font-normal text-black text-opacity-60">
            Alamat
          </div>
          <div className="mt-1.5 text-label-lg">{sellerData?.address}</div>
          <div className="mt-1.5 text-left text-label-md self-start">
            {sellerData?.additionalAddress}
          </div>
        </div>
        <button
          className="px-4 py-2 mx-2 rounded-lg select-none text-primary"
          onClick={() => copyToClipboard({ value: sellerData?.address })}
        >
          Salin
        </button>
      </div>
    </>
  );

  return (
    <div className="flex flex-col items-center text-center mx-auto h-full bg-white">
      {renderAvatar()}
      {renderStoreProductOrderCount()}
      {renderContactInfo()}
      {renderAddressInfo()}
      {showImageViewer && (
        <ImageViewer
          images={[
            {
              imageUrl: sellerData?.profilePicture
                ? sellerData?.profilePicture
                : "/img/common/default-merchant.svg",
              alt: sellerData?.User.name,
            },
          ]}
          onClose={() => setShowImageViewer(false)}
          initialIndex={0}
        />
      )}
    </div>
  );
};
